import { useState, useEffect, useMemo, useRef } from "react";

import { Polygon, MultiPolygon } from "@turf/helpers";
import { proxy } from "comlink";
import { parse as telejsonParse } from "telejson";

import { HindcastMetadata } from "@sofarocean/weather-cubes";
import { WeatherContextType } from ".";

// import { AnalyticsEvent } from "../Analytics";
import { useAppUrl } from "../../helpers/use-app-url";
import {
  getWeatherMapVariables,
  getWeatherPlotVariables,
} from "../../helpers/weather";
import { isEqual } from "lodash";
import AsyncWeatherProviderProxy from "web-workers/weather-worker/AsyncWeatherProviderProxy";

type WeatherStateProps = {
  timeAtPageLoad: Date;
};

export default function useWeatherState({ timeAtPageLoad }: WeatherStateProps) {
  // Allow us to set a time in the query parameters to retrieve a historical
  // forecast
  const { simulatedHistoricalTime } = useAppUrl();
  let simulatedHistoricalTimeString = simulatedHistoricalTime?.toISOString();

  // This state is managed by the StatefulHindcastProvider instance
  // Later on, we register a callback to get updates whenever it changes
  const [mapWeatherMetadata, setMapWeatherMetadata] =
    useState<HindcastMetadata>({
      downloadError: null,
      downloadStatus: "idle",
      downloadProgress: 0,
      weatherVariables: [],
      hindcastUUID: "",
      initialized: false,
    });
  const [plotWeatherMetadata, setPlotWeatherMetadata] =
    useState<HindcastMetadata>({
      downloadError: null,
      downloadStatus: "idle",
      downloadProgress: 0,
      weatherVariables: [],
      hindcastUUID: "",
      initialized: false,
    });

  const mapWeatherProviderRef = useRef<AsyncWeatherProviderProxy>();
  const mapWeatherProvider = mapWeatherProviderRef.current;
  const mapRegionOfInterest = useRef<Polygon | MultiPolygon | null>(null);

  const plotWeatherProviderRef = useRef<AsyncWeatherProviderProxy>();
  const plotWeatherProvider = plotWeatherProviderRef.current;
  const plotRegionOfInterest = useRef<Polygon | MultiPolygon | null>(null);

  useEffect(() => {
    const initializeWeatherProviders = async () => {
      const timestring =
        typeof simulatedHistoricalTimeString === "string"
          ? simulatedHistoricalTimeString
          : undefined;

      const baseTime = simulatedHistoricalTimeString
        ? +new Date(simulatedHistoricalTimeString)
        : timeAtPageLoad.getTime();
      // load 7 days in the past and 7 days in the future
      const startTime = new Date(baseTime - 7 * 24 * 60 * 60 * 1000);
      const endTime = new Date(baseTime + 7 * 24 * 60 * 60 * 1000);

      const mapWeatherVariables = getWeatherMapVariables();
      const plotWeatherVariables = getWeatherPlotVariables();

      // This `await` is required since the AsyncWeatherProvider is created
      // asynchronously in the web worker via `comlink`. Typescript doesn't know
      // that at this point but it's true!
      mapWeatherProviderRef.current = new AsyncWeatherProviderProxy(
        proxy((meta) => setMapWeatherMetadata(telejsonParse(meta))),
        mapWeatherVariables,
        startTime,
        endTime,
        timestring
      );
      // Provider needs to be initialized before components can use it
      await mapWeatherProviderRef.current.init();

      plotWeatherProviderRef.current = new AsyncWeatherProviderProxy(
        proxy((meta) => setPlotWeatherMetadata(telejsonParse(meta))),
        plotWeatherVariables,
        startTime,
        endTime,
        timestring
      );
      // Provider needs to be initialized before components can use it
      await plotWeatherProviderRef.current.init();
    };

    initializeWeatherProviders();
    return () => {
      mapWeatherProviderRef.current?.dispose();
      plotWeatherProviderRef.current?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapWeatherProviderRef, plotWeatherProviderRef]);

  // Update simulated historical time when it changes
  useEffect(() => {
    const timestring =
      typeof simulatedHistoricalTimeString === "string"
        ? simulatedHistoricalTimeString
        : undefined;

    mapWeatherProviderRef.current?.setSimulatedHistoricalTime(timestring);
    plotWeatherProviderRef.current?.setSimulatedHistoricalTime(timestring);
  }, [simulatedHistoricalTimeString]);

  // Memoize callbacks to the weather provider
  const providerCallbacks = useMemo(() => {
    if (mapWeatherProvider && plotWeatherProvider) {
      return {
        setMapRegionOfInterest: (roi: Polygon | MultiPolygon | null) => {
          // Only set this once, on load
          if (!isEqual(mapRegionOfInterest.current, roi)) {
            mapWeatherProvider.setRegionOfInterest(roi);
            mapRegionOfInterest.current = roi;
          }
        },
        setPlotRegionOfInterest: (roi: Polygon | MultiPolygon | null) => {
          // Only set this once, on load
          if (!isEqual(plotRegionOfInterest.current, roi)) {
            plotWeatherProvider.setRegionOfInterest(roi);
            plotRegionOfInterest.current = roi;
          }
        },
        getHindcastValue: mapWeatherProvider.getHindcastValue,

        // these are only used for generating plots & spotter trail
        getHindcastTimeSeries: plotWeatherProvider.getHindcastTimeSeries,
        getRequestTimes: plotWeatherProvider.getRequestTimes,

        // trying to cut down on map loading time
        setWeatherVariables: mapWeatherProvider.setWeatherVariables,

        // these are used for rendering the map
        calculateWeatherVariableExtremaLabels:
          mapWeatherProvider.calculateWeatherVariableExtremaLabels.bind(
            mapWeatherProvider
          ),
        generateTilesAsync: mapWeatherProvider.generateTiles,
        buildWebGLBuffer:
          mapWeatherProvider.buildWebGLBuffer.bind(mapWeatherProvider),
      };
    } else {
      return {
        setMapRegionOfInterest: async () => null,
        setPlotRegionOfInterest: async () => null,
        getHindcastValue: async () => null,
        getHindcastTimeSeries: async () => undefined,
        getRequestTimes: async () => undefined,
        setWeatherVariables: async () => undefined,
        calculateWeatherVariableExtremaLabels: async () => undefined,
        generateTilesAsync: async () => [],
        buildWebGLBuffer: async () => "NO_DATA" as const,
      };
    }
  }, [mapWeatherProvider, plotWeatherProvider]);

  // Give access to the weather provider through the WeatherContext
  const weatherValue: WeatherContextType = useMemo(() => {
    return {
      getHindcastValue: providerCallbacks.getHindcastValue,
      getHindcastTimeSeries: providerCallbacks.getHindcastTimeSeries,
      getRequestTimes: providerCallbacks.getRequestTimes,
      setWeatherVariables: providerCallbacks.setWeatherVariables,
      calculateWeatherVariableExtremaLabels:
        providerCallbacks.calculateWeatherVariableExtremaLabels,
      generateTilesAsync: providerCallbacks.generateTilesAsync,
      setMapRegionOfInterest: providerCallbacks.setMapRegionOfInterest,
      setPlotRegionOfInterest: providerCallbacks.setPlotRegionOfInterest,
      buildWebGLBuffer: providerCallbacks.buildWebGLBuffer,
      currentMapMetadata: mapWeatherMetadata,
      currentPlotMetadata: plotWeatherMetadata,
    };
  }, [providerCallbacks, mapWeatherMetadata, plotWeatherMetadata]);

  return weatherValue;
}
